import { DateTime } from "luxon";
import { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";

export type ResourceBookDaySegmentKind = "past" | "unavailable" | "available";

export type ResourceBookDaySegment = {
  /** Segment start as millis for sorting / merging */
  startMs: number;
  /** Segment end as millis (exclusive of last instant is not needed; end > start) */
  endMs: number;
  /**
   * - `past`: already elapsed on this calendar day (display grey)
   * - `unavailable`: booked or still inside booking lead time (display red)
   * - `available`: free to book (display green)
   */
  kind: ResourceBookDaySegmentKind;
};

type Interval = { start: DateTime; end: DateTime };

function clipIntervalToDay(
  start: DateTime,
  end: DateTime,
  dayStart: DateTime,
  dayEndExclusive: DateTime
): Interval | null {
  const s = start < dayStart ? dayStart : start;
  const e = end > dayEndExclusive ? dayEndExclusive : end;
  if (e <= s) {
    return null;
  }
  return { start: s, end: e };
}

function mergeIntervals(intervals: Interval[]): Interval[] {
  if (intervals.length === 0) return [];
  const sorted = [...intervals].sort((a, b) => a.start.valueOf() - b.start.valueOf());
  const merged: Interval[] = [];
  let cur = sorted[0];
  for (let i = 1; i < sorted.length; i += 1) {
    const next = sorted[i];
    if (next.start <= cur.end) {
      cur = { start: cur.start, end: DateTime.max(cur.end, next.end) };
    } else {
      merged.push(cur);
      cur = next;
    }
  }
  merged.push(cur);
  return merged;
}

/**
 * Lists each calendar day from `fromIso` through `toIso` inclusive (yyyy-MM-dd).
 */
export function listIsoDaysInclusive(fromIso: string, toIso: string): string[] {
  const start = DateTime.fromISO(fromIso);
  const end = DateTime.fromISO(toIso);
  if (!start.isValid || !end.isValid || end < start) {
    return [];
  }
  const days: string[] = [];
  for (let d = start; d <= end; d = d.plus({ days: 1 })) {
    const iso = d.toISODate();
    if (iso) days.push(iso);
  }
  return days;
}

/**
 * Removes zero-length segments. When nothing usable remains on a calendar day with
 * positive length, returns a single full-day free segment.
 */
export function finalizeResourceBookDaySegments(
  segments: ResourceBookDaySegment[],
  dayStart: DateTime,
  dayEndExclusive: DateTime
): ResourceBookDaySegment[] {
  const filtered = segments.filter(seg => seg.endMs > seg.startMs);
  const dayLenMs = dayEndExclusive.valueOf() - dayStart.valueOf();
  if (filtered.length === 0 && dayLenMs > 0) {
    return [
      {
        startMs: dayStart.valueOf(),
        endMs: dayEndExclusive.valueOf(),
        kind: "available"
      }
    ];
  }
  return filtered;
}

/**
 * Marks `[dayStart, now)` as `past` on the viewed day. Entire past days become one past band.
 * Future days are unchanged.
 */
export function applyPastKindToResourceBookDaySegments(
  segments: ResourceBookDaySegment[],
  dayStart: DateTime,
  dayEndExclusive: DateTime,
  now: DateTime,
  timezone: string
): ResourceBookDaySegment[] {
  const localNow = now.setZone(timezone);
  if (!localNow.isValid || !dayStart.isValid) {
    return segments;
  }

  if (localNow <= dayStart) {
    return segments;
  }

  if (localNow >= dayEndExclusive) {
    return [
      {
        startMs: dayStart.valueOf(),
        endMs: dayEndExclusive.valueOf(),
        kind: "past"
      }
    ];
  }

  const pastEndMs = localNow.valueOf();
  const out: ResourceBookDaySegment[] = [];

  for (const seg of segments) {
    if (seg.endMs <= pastEndMs) {
      out.push({ ...seg, kind: "past" });
      continue;
    }
    if (seg.startMs >= pastEndMs) {
      out.push(seg);
      continue;
    }
    out.push({
      startMs: seg.startMs,
      endMs: pastEndMs,
      kind: "past"
    });
    out.push({
      startMs: pastEndMs,
      endMs: seg.endMs,
      kind: seg.kind
    });
  }

  return finalizeResourceBookDaySegments(out, dayStart, dayEndExclusive);
}

export type ResourceBookAvailabilityOptions = {
  bookingLeadTimeHours?: number | null;
  /** Injected for tests; defaults to `DateTime.now()`. */
  now?: DateTime;
};

/** Earliest bookable instant in `timezone`, or `null` when lead time is absent/zero. */
export function resolveEarliestBookableLocal(
  timezone: string,
  bookingLeadTimeHours: number | null | undefined,
  now: DateTime = DateTime.now()
): DateTime | null {
  const hours = bookingLeadTimeHours ?? 0;
  if (hours <= 0) {
    return null;
  }
  const localNow = now.setZone(timezone);
  if (!localNow.isValid) {
    return null;
  }
  return localNow.plus({ hours });
}

/** Ceil a datetime to the next `stepMinutes` boundary (e.g. Mobiscroll `stepMinute={15}`). */
export function ceilDateTimeToStepMinutes(dt: DateTime, stepMinutes = 15): DateTime {
  if (!dt.isValid || stepMinutes <= 0) {
    return dt;
  }
  const stepMs = stepMinutes * 60_000;
  const ceiledMs = Math.ceil(dt.toMillis() / stepMs) * stepMs;
  return DateTime.fromMillis(ceiledMs, { zone: dt.zone });
}

/**
 * Mobiscroll `valid` ranges for the booking **start** time on `bookingFromIso`.
 * Empty array means the whole start day is still inside the lead-time window.
 */
export function resourceBookTimePickerValidRanges(opts: {
  bookingFromIso: string;
  timezone: string;
  bookingLeadTimeHours?: number | null;
  now?: DateTime;
  stepMinutes?: number;
}): { end: string; start: string }[] {
  const earliest = resolveEarliestBookableLocal(opts.timezone, opts.bookingLeadTimeHours, opts.now);
  if (!earliest) {
    return [{ start: "00:00", end: "23:59" }];
  }

  const dayStart = DateTime.fromISO(opts.bookingFromIso, { zone: opts.timezone }).startOf("day");
  if (!dayStart.isValid) {
    return [{ start: "00:00", end: "23:59" }];
  }

  const dayIso = dayStart.toISODate();
  const earliestIso = earliest.toISODate();
  if (!dayIso || !earliestIso) {
    return [{ start: "00:00", end: "23:59" }];
  }

  if (earliestIso < dayIso) {
    return [{ start: "00:00", end: "23:59" }];
  }
  if (earliestIso > dayIso) {
    return [];
  }

  const step = opts.stepMinutes ?? 15;
  const ceiled = ceilDateTimeToStepMinutes(earliest, step);
  if ((ceiled.toISODate() ?? "") > dayIso) {
    return [];
  }
  return [{ start: ceiled.toFormat("HH:mm"), end: "23:59" }];
}

/**
 * Bumps chosen start (and same-day end if needed) so the start is not inside the lead window.
 */
export function clampResourceBookChosenTimesToLeadTime(opts: {
  bookingFromIso: string;
  bookingToIso: string;
  chosenTimes: { endTime: string; startTime: string };
  timezone: string;
  bookingLeadTimeHours?: number | null;
  now?: DateTime;
  stepMinutes?: number;
}): { endTime: string; startTime: string } {
  const ranges = resourceBookTimePickerValidRanges({
    bookingFromIso: opts.bookingFromIso,
    timezone: opts.timezone,
    bookingLeadTimeHours: opts.bookingLeadTimeHours,
    now: opts.now,
    stepMinutes: opts.stepMinutes
  });
  if (ranges.length === 0) {
    return opts.chosenTimes;
  }

  const minStart = ranges[0]?.start ?? "00:00";
  if (opts.chosenTimes.startTime >= minStart) {
    return opts.chosenTimes;
  }

  const newStart = minStart;
  let newEnd = opts.chosenTimes.endTime;
  if (opts.bookingFromIso === opts.bookingToIso && newEnd <= newStart) {
    const startDt = DateTime.fromISO(`${opts.bookingFromIso}T${newStart}`, {
      zone: opts.timezone
    });
    if (startDt.isValid) {
      const bumped = startDt.plus({ hours: 1 });
      newEnd = bumped.toISODate() === opts.bookingFromIso ? bumped.toFormat("HH:mm") : "23:59";
    }
  }

  return { startTime: newStart, endTime: newEnd };
}

/** Clock label for a day segment; exclusive next-midnight end shows as `24:00`. */
export function formatResourceBookSegmentClockRange(
  startMs: number,
  endMs: number,
  dayIso: string,
  timezone: string
): string {
  const start = DateTime.fromMillis(startMs).setZone(timezone);
  const end = DateTime.fromMillis(endMs).setZone(timezone);
  const dayEndExclusive = DateTime.fromISO(dayIso, { zone: timezone }).startOf("day").plus({
    days: 1
  });
  const endLabel =
    dayEndExclusive.isValid && end.valueOf() === dayEndExclusive.valueOf()
      ? "24:00"
      : end.toFormat("HH:mm");
  return `${start.toFormat("HH:mm")} - ${endLabel}`;
}

/**
 * Builds merged booked intervals and free gaps for one calendar day in `timezone`,
 * using schedule `startDate` / `endDate` ISO strings (interpreted then placed in zone).
 * Optional booking lead time marks `[dayStart, earliest)` as unavailable.
 * Elapsed time on the day is then split out as `past` (grey in the UI).
 */
export function buildResourceBookDayAvailabilitySegments(
  dayIso: string,
  schedules: BookingScheduleInterface[],
  timezone: string,
  options?: ResourceBookAvailabilityOptions
): ResourceBookDaySegment[] {
  const dayStart = DateTime.fromISO(dayIso, { zone: timezone }).startOf("day");
  const dayEndExclusive = dayStart.plus({ days: 1 });
  const now = options?.now ?? DateTime.now();

  const bookedRaw: Interval[] = [];

  const earliest = resolveEarliestBookableLocal(timezone, options?.bookingLeadTimeHours, now);
  if (earliest?.isValid && earliest > dayStart) {
    const leadEnd = DateTime.min(earliest, dayEndExclusive);
    if (leadEnd > dayStart) {
      bookedRaw.push({ start: dayStart, end: leadEnd });
    }
  }

  for (const s of schedules) {
    const start = DateTime.fromISO(s.startDate).setZone(timezone);
    const end = DateTime.fromISO(s.endDate).setZone(timezone);
    if (!start.isValid || !end.isValid) continue;
    const clipped = clipIntervalToDay(start, end, dayStart, dayEndExclusive);
    if (clipped) bookedRaw.push(clipped);
  }

  const booked = mergeIntervals(bookedRaw);
  const segments: ResourceBookDaySegment[] = [];

  let cursor = dayStart;
  for (const b of booked) {
    if (b.start > cursor) {
      segments.push({
        startMs: cursor.valueOf(),
        endMs: b.start.valueOf(),
        kind: "available"
      });
    }
    segments.push({
      startMs: b.start.valueOf(),
      endMs: b.end.valueOf(),
      kind: "unavailable"
    });
    cursor = DateTime.max(b.end, cursor);
  }
  if (cursor < dayEndExclusive) {
    segments.push({
      startMs: cursor.valueOf(),
      endMs: dayEndExclusive.valueOf(),
      kind: "available"
    });
  }

  const finalized = finalizeResourceBookDaySegments(segments, dayStart, dayEndExclusive);
  return applyPastKindToResourceBookDaySegments(
    finalized,
    dayStart,
    dayEndExclusive,
    now,
    timezone
  );
}

export function segmentDurationMinutes(seg: ResourceBookDaySegment): number {
  return (seg.endMs - seg.startMs) / 60_000;
}

/** Full calendar day length in minutes (for relative bar width). */
export function fullDayMinutes(): number {
  return 24 * 60;
}
